import React from 'react';
import { cn } from '../../lib/utils';
import { CheckCircleFilledIcon, XCircleFilledIcon, CircleIcon } from '../ui/Icons';
import { badgeClass, getBadgeStyleTokens } from '../../config/tableBadgeStyles';
import { isRefinedTableUi } from '../../config/tableUiThemeRuntime';
import { CELL_RENDERERS } from '../tables/tableCellRenderers';

const BADGE_CELL_RENDERERS = new Set([
  'badge',
  'statusBadge',
  'booleanBadge',
  'priorityBadge',
  'entityTypeBadge',
  'approvalTypeBadge',
  'workflowTypeBadge',
]);

/** Whether a table cellRenderer should render as a badge in split view. */
export function isBadgeCellRenderer(rendererKey) {
  if (!rendererKey || typeof rendererKey !== 'string') return false;
  return BADGE_CELL_RENDERERS.has(rendererKey) || rendererKey.endsWith('Badge');
}

export function parseTaskList(rawVal) {
  if (Array.isArray(rawVal)) return rawVal;
  if (typeof rawVal === 'string' && rawVal.trim()) {
    try {
      const parsed = JSON.parse(rawVal);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function pickScalarLabel(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function pickRelationLabel(rel) {
  if (!rel || typeof rel !== 'object' || Array.isArray(rel)) return null;
  if (rel.userDisplayName) return rel.userDisplayName;
  if (rel.userFirstName && rel.userLastName) {
    return `${rel.userFirstName} ${rel.userLastName}`.trim();
  }
  const scalar =
    pickScalarLabel(rel.code) ||
    pickScalarLabel(rel.name) ||
    pickScalarLabel(rel.title) ||
    pickScalarLabel(rel.typeName) ||
    pickScalarLabel(rel.statusName) ||
    pickScalarLabel(rel.label) ||
    pickScalarLabel(rel.version) ||
    (rel.revisionNumber != null ? String(rel.revisionNumber) : null) ||
    pickScalarLabel(rel.userEmail) ||
    pickScalarLabel(rel.email);
  if (scalar) return scalar;
  // Nested revision-shaped objects (id, version, dateIssued, issuedBy, …)
  if (rel.dateIssued || rel.issuedBy) {
    const parts = [
      pickScalarLabel(rel.version),
      rel.dateIssued ? new Date(rel.dateIssued).toLocaleDateString() : null,
      typeof rel.issuedBy === 'string'
        ? rel.issuedBy
        : pickRelationLabel(rel.issuedBy),
    ].filter(Boolean);
    if (parts.length) return parts.join(' · ');
  }
  return null;
}

/** Safe string for labels in approval summaries, audit rows, etc. */
export function formatFieldDisplayValue(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) return value.toLocaleDateString();
  if (typeof value === 'object') {
    return pickRelationLabel(value);
  }
  return String(value);
}

/**
 * Best relation object for badges (status, lookups) — prefers nested entity over flattened string.
 */
export function resolveRelationForField(field, record) {
  if (!field || !record) return null;

  const displayField = field.table?.displayField;
  if (displayField) {
    const val = record[displayField];
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      return val;
    }
  }

  if (/Id$/.test(field.name)) {
    const base = field.name.replace(/Id$/, '');
    const rel = record[base];
    if (rel && typeof rel === 'object' && !Array.isArray(rel)) {
      return rel;
    }
  }

  return null;
}

/** Status / priority pills in custom split-view headers (e.g. Approval). */
export function splitViewStatusBadgeClass(status) {
  const tokens = getBadgeStyleTokens();
  const key = String(status?.name || status || '').toLowerCase();
  return badgeClass(tokens.status[key] || tokens.status.cancelled || tokens.neutral);
}

export function splitViewPriorityBadgeClass(priority) {
  const tokens = getBadgeStyleTokens();
  const key = String(priority?.name || priority || '').toLowerCase();
  return badgeClass(tokens.priority[key] || tokens.priority.default);
}

export function splitViewTabCountBadgeClass() {
  const tokens = getBadgeStyleTokens();
  return isRefinedTableUi()
    ? badgeClass(tokens.blue)
    : 'inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold text-white rounded-full min-w-[18px] h-4 bg-gray-600';
}

/**
 * Resolve raw/display values and which table cell renderer to use (if any).
 */
export function resolveSplitViewField(field, record) {
  let rawVal = record[field.name];
  const isEmpty = rawVal == null || rawVal === '';
  let displayVal = rawVal;
  let rendererKey = field.table?.cellRenderer || null;
  let isBadge = false;
  let isStatus = false;

  const relation = resolveRelationForField(field, record);
  const displayField = field.table?.displayField;

  if (displayField && record[displayField] != null) {
    displayVal = record[displayField];
    if (isBadgeCellRenderer(rendererKey)) {
      isBadge = true;
      isStatus = rendererKey === 'statusBadge';
    } else if (!rendererKey) {
      isBadge = true;
      isStatus = false;
    }
  } else if (/Id$/.test(field.name) && relation) {
    displayVal = pickRelationLabel(relation) ?? displayVal;
    isBadge = !rendererKey || isBadgeCellRenderer(rendererKey);
    isStatus = rendererKey === 'statusBadge';
    rawVal = relation;
  }

  if ((field.type === 'switch' || field.type === 'boolean') && !rendererKey) {
    rendererKey = 'booleanBadge';
  } else if (isStatus && !rendererKey) {
    rendererKey = 'statusBadge';
  } else if (isBadge && !rendererKey) {
    rendererKey = 'badge';
  } else if (rendererKey === 'toggleSwitch') {
    rendererKey = 'booleanBadge';
  }

  if (
    !rendererKey &&
    relation &&
    (relation.userDisplayName || relation.userEmail || /UserId$|ById$/.test(field.name))
  ) {
    rendererKey = 'userDisplay';
  }

  if (field.type === 'date' && !isEmpty && !rendererKey) {
    try {
      displayVal = new Date(rawVal).toLocaleDateString();
    } catch {
      /* keep */
    }
  }

  if (field.type === 'number' && rawVal != null && !rendererKey) {
    if (field.step === '0.01' || field.step === 0.01) {
      displayVal = parseFloat(rawVal).toFixed(2);
    } else {
      displayVal = String(rawVal);
    }
  }

  let valueForRenderer = rawVal;
  if (isBadgeCellRenderer(rendererKey) && relation) {
    valueForRenderer = relation;
  } else if (displayField && record[displayField] != null) {
    const displayFieldValue = record[displayField];
    valueForRenderer =
      typeof displayFieldValue === 'object' && !Array.isArray(displayFieldValue)
        ? displayFieldValue
        : relation || displayFieldValue;
  } else if (relation) {
    valueForRenderer = relation;
  }

  if (
    rendererKey === 'date' ||
    rendererKey === 'optionalDate' ||
    rendererKey === 'dateTime' ||
    rendererKey === 'datetime'
  ) {
    valueForRenderer = rawVal;
  }

  // Never use table truncating renderers (lineClamp, etc.) in split view for long text.
  if (isSplitViewFullWidthField(field, field.name)) {
    rendererKey = null;
    isBadge = false;
    isStatus = false;
    displayVal = rawVal;
    valueForRenderer = rawVal;
  }

  return {
    rawVal,
    displayVal,
    isEmpty,
    rendererKey,
    valueForRenderer,
    isBadge,
    isStatus,
  };
}

/**
 * Badge / boolean cell — delegates to shared split-view field renderer.
 * @deprecated Prefer renderSplitViewFieldValue; kept for legacy DetailsTab call sites.
 */
export function renderSplitViewBadgeOrBoolean({
  field,
  record,
  theme,
  rawVal: _rawVal,
  displayVal: _displayVal,
  isBadge: _isBadge,
  isStatus: _isStatus,
  isBoolean: _isBoolean,
}) {
  if (!field || !record) return null;
  if (isSplitViewFullWidthField(field, field.name)) return null;
  const ctx = resolveSplitViewField(field, record);
  if (ctx.rendererKey && CELL_RENDERERS[ctx.rendererKey]) {
    return renderSplitViewFieldValue(field, record, theme);
  }
  if (
    _isBoolean ||
    field.type === 'switch' ||
    field.type === 'boolean' ||
    field.table?.cellRenderer === 'booleanBadge'
  ) {
    return renderSplitViewLegacyBoolean(record[field.name], theme);
  }
  return null;
}

/** Textarea / description fields span the full split-view grid row. */
export function isSplitViewFullWidthField(field, fieldName) {
  if (!field) return fieldName === 'description';
  return field.type === 'textarea' || fieldName === 'description';
}

/** Map form fieldSpans (12-col) to split-view grid; full-row fields use col-span-full. */
export function splitViewFieldColSpanClass(field, fieldName, section) {
  const configured = section?.fieldSpans?.[fieldName];
  if (configured && /col-span-12|col-span-full/.test(configured)) {
    return 'col-span-full';
  }
  if (isSplitViewFullWidthField(field, fieldName)) {
    return 'col-span-full';
  }
  return '';
}

export function splitViewFieldValueContainerClass(field, fieldName, theme, extra = '') {
  const fullWidth = isSplitViewFullWidthField(field, fieldName);
  return cn(
    'text-sm rounded p-2',
    theme.colors.background,
    theme.text.primary,
    fullWidth ? 'block w-full min-w-0' : 'flex items-center gap-2',
    extra,
  );
}

export function splitViewTextareaValueClass() {
  return 'block w-full whitespace-pre-wrap break-words';
}

/**
 * Render a single field value for split-view / details tabs (theme-aware badges).
 */
export function renderSplitViewFieldValue(field, record, theme, options = {}) {
  const { fieldOverride } = options;

  if (fieldOverride) {
    const custom = fieldOverride(field, record, theme);
    if (custom !== undefined) return custom;
  }

  const ctx = resolveSplitViewField(field, record);
  const column = {
    ...(field.table || {}),
    key: field.name,
    header: field.label,
  };

  // Split view: Yes/No with green check / red X (not refined table pills)
  if (
    ctx.rendererKey === 'booleanBadge' ||
    field.type === 'switch' ||
    field.type === 'boolean'
  ) {
    return renderSplitViewLegacyBoolean(
      ctx.rawVal ?? record[field.name],
      theme,
    );
  }

  if (ctx.rendererKey && CELL_RENDERERS[ctx.rendererKey]) {
    const rendered = CELL_RENDERERS[ctx.rendererKey](
      ctx.valueForRenderer,
      column,
      record,
      theme,
    );
    return <div className="split-view-cell-content w-full">{rendered}</div>;
  }

  if (field.type === 'taskList') {
    const tasks = parseTaskList(ctx.rawVal)
      .map((task) => ({
        task: String(task?.task ?? task?.Task ?? '').trim(),
        isDone: Boolean(task?.isDone ?? task?.IsDone),
      }))
      .filter((task) => task.task);

    if (tasks.length === 0) {
      return <span className={theme.text.muted}>—</span>;
    }

    const tokens = getBadgeStyleTokens();
    return (
      <div className="w-full space-y-1">
        {tasks.map((task, idx) => (
          <div key={`${field.name}-task-${idx}`} className="flex items-center justify-between gap-2">
            <span className={cn('text-sm', task.isDone ? 'line-through opacity-70' : '')}>
              {task.task}
            </span>
            <span
              className={badgeClass(
                task.isDone ? tokens.emerald : (tokens.amber ?? tokens.neutral),
              )}
            >
              {task.isDone ? 'Done' : 'Open'}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (field.name === 'implementationEvaluationRequiredMonths') {
    const months = Number(record?.[field.name] ?? ctx.rawVal);
    if (months === 3 || months === 6) {
      const tokens = getBadgeStyleTokens();
      return (
        <span className={badgeClass(tokens.blue ?? tokens.neutral)}>
          {months} months
        </span>
      );
    }
    return <span className={theme.text.muted}>—</span>;
  }

  if (field.type === 'textarea' || field.name === 'description') {
    return (
      <span className={splitViewTextareaValueClass()}>
        {String(ctx.displayVal ?? '—')}
      </span>
    );
  }

  if (ctx.isEmpty) {
    return options.emptyLabel ? (
      <span className="italic">{options.emptyLabel}</span>
    ) : (
      <span className={theme.text.muted}>—</span>
    );
  }

  return (
    <span className={theme.text.primary}>{String(ctx.displayVal ?? '—')}</span>
  );
}

/** Legacy boolean row (icons) when refined uses booleanBadge via CELL_RENDERERS. */
export function renderSplitViewLegacyBoolean(rawVal, theme) {
  if (rawVal === true) {
    return (
      <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
        <CheckCircleFilledIcon className="w-4 h-4" />
        <span className="text-sm font-medium">Yes</span>
      </span>
    );
  }
  if (rawVal === false) {
    return (
      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
        <XCircleFilledIcon className="w-4 h-4" />
        <span className="text-sm font-medium">No</span>
      </span>
    );
  }
  return (
    <span className={cn('inline-flex items-center gap-1', theme.text.muted)}>
      <CircleIcon className="w-4 h-4" />
      <span>—</span>
    </span>
  );
}
